import { useState } from "react";
import { Button, useToast } from "@chakra-ui/react";
import { useActiveAccount } from "applesauce-react/hooks";
import { SimpleSigner, PasswordSigner } from "applesauce-signers";

import Fingerprint04 from "../icons/fingerprint-04";
import { isPasskeySupported, registerPasskey } from "../../services/passkey";

function platformLabel(): string {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return "iPhone";
  if (/Mac/.test(ua)) return "Mac";
  if (/Android/.test(ua)) return "Android";
  if (/Windows/.test(ua)) return "Windows";
  return "Passkey";
}

export default function PasskeySetupButton() {
  const account = useActiveAccount();
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  if (!account || !isPasskeySupported()) return null;

  const signer = account.signer;
  const hasKey =
    (signer instanceof SimpleSigner && signer.key) || (signer instanceof PasswordSigner && signer.key);
  if (!hasKey) return null;

  const handleSetup = async () => {
    setLoading(true);
    try {
      const key = (signer as InstanceType<typeof SimpleSigner> | InstanceType<typeof PasswordSigner>).key;
      if (!key) throw new Error("Secret key not available. Unlock your account first.");
      await registerPasskey(key, account.pubkey, platformLabel());
      toast({ description: "Passkey saved! You can now unlock with biometrics.", status: "success", duration: 5000 });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to register passkey";
      if (!message.includes("cancelled")) {
        toast({ description: message, status: "error", duration: 8000 });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      leftIcon={<Fingerprint04 boxSize={5} />}
      onClick={handleSetup}
      isLoading={loading}
      loadingText="Setting up..."
      variant="outline"
      mt="2"
    >
      Save as Passkey
    </Button>
  );
}
